/**
 * The CxL3 test catalogue for LV switchboards — the starting portfolio.
 *
 * **CxL3 only.** Energised, functional testing. Nothing dead-bus belongs here:
 * insulation resistance, continuity and polarity are CxL2 and were deliberately
 * left out, because a script that mixes them puts a dead-bus check in front of
 * an engineer standing at a live board.
 *
 * This is a starting set, not a finished one. The catalogue is meant to grow —
 * generators, UPS, ATS, chillers, each with its own tests — and adding one is a
 * data change. What must not drift is the shape: every test names the equipment
 * it applies to, the scopes it may be taken at, and the photos it requires per
 * unit of that scope, so a project's selection can be costed before anyone
 * commits to it.
 *
 * Numbers here are the values MY02 and MY03 ran with. They are the *defaults* a
 * project starts from, not fixed limits: the site admin moves any of them per
 * project, and the default is kept so it can be put back.
 */

import type { CatalogueTest } from './catalogue.js';

/** Bumped whenever a test is added or changed, so a project records what it chose from. */
export const CATALOGUE_VERSION = '2026-09-20';

const MV_SOURCES = ['DPM', 'PQA', 'BMS'] as const;
const mvShots = (group: string): string[] => MV_SOURCES.map((s) => `MV-${group}-${s}`);

export const CXL3_LV_SWITCHBOARD: readonly CatalogueTest[] = [
  {
    code: 'VIS-BOARD',
    name: 'Visual inspection and readiness',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    scopes: ['board'],
    defaultSelected: true,
    section: 'Visual Inspection',
    note: 'Asked once per board. Per breaker it would run 192 times on MY02.',
    checks: [
      { label: 'Equipment tagging and labels match the drawing', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Cable entrances sealed', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Earthing and bonding connections tight and labelled', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Panel free of tools, debris and temporary works', responseType: 'pass_fail_na', toleranceMode: 'none' },
      { label: 'Energised under an approved MOP', responseType: 'yes_no_applicable', toleranceMode: 'none' },
    ],
    shotCodes: ['VIS-FRONT', 'VIS-INTERNAL', 'VIS-LABEL'],
  },
  {
    code: 'VT-3PH',
    name: 'Voltage test — three phase',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    scopes: ['board', 'lineup'],
    defaultSelected: true,
    section: 'Voltage Test',
    checks: [
      { label: 'L1-L2', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'L2-L3', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'L3-L1', responseType: 'measured_pass_fail', unit: 'V', nominal: 415, tolerance: 5, toleranceMode: 'percent' },
      { label: 'L1-N', responseType: 'measured_pass_fail', unit: 'V', nominal: 240, tolerance: 10, toleranceMode: 'percent' },
      { label: 'L2-N', responseType: 'measured_pass_fail', unit: 'V', nominal: 240, tolerance: 10, toleranceMode: 'percent' },
      { label: 'L3-N', responseType: 'measured_pass_fail', unit: 'V', nominal: 240, tolerance: 10, toleranceMode: 'percent' },
      { label: 'N-E', responseType: 'measured_pass_fail', unit: 'V', tolerance: 5, toleranceMode: 'max', acceptanceCriteria: '≤ 5 V' },
    ],
    shotCodes: ['VT-METER'],
  },
  {
    code: 'FREQ',
    name: 'Supply frequency',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    scopes: ['board'],
    defaultSelected: true,
    section: 'Frequency',
    checks: [
      // ±1 Hz, not ±1%: the absolute mode exists for exactly this.
      { label: 'Frequency', responseType: 'measured_pass_fail', unit: 'Hz', nominal: 50, tolerance: 1, toleranceMode: 'absolute' },
    ],
  },
  {
    code: 'PR',
    name: 'Phase rotation',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    scopes: ['breaker', 'lineup', 'board'],
    defaultSelected: true,
    section: 'Phase Rotation',
    note: 'Taken at each way by default — a transposition downstream of the incomer is exactly what this finds.',
    checks: [
      { label: 'Phase rotation', responseType: 'pass_fail_na', toleranceMode: 'none', acceptanceCriteria: 'Clockwise, L1-L2-L3', requiresPhoto: true },
    ],
    shotCodes: ['PR'],
  },
  {
    code: 'ELI',
    name: 'Earth loop impedance',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    scopes: ['breaker', 'lineup'],
    defaultSelected: true,
    section: 'Earth Loop Impedance',
    checks: [
      { label: 'Earth loop impedance R – E', responseType: 'measured_pass_fail', unit: 'Ω', tolerance: 1, toleranceMode: 'max', acceptanceCriteria: '< 1 Ω', requiresPhoto: true },
      { label: 'Earth loop impedance Y – E', responseType: 'measured_pass_fail', unit: 'Ω', tolerance: 1, toleranceMode: 'max', acceptanceCriteria: '< 1 Ω', requiresPhoto: true },
      { label: 'Earth loop impedance B – E', responseType: 'measured_pass_fail', unit: 'Ω', tolerance: 1, toleranceMode: 'max', acceptanceCriteria: '< 1 Ω', requiresPhoto: true },
    ],
    shotCodes: ['ELI-R-E', 'ELI-Y-E', 'ELI-B-E'],
  },
  {
    code: 'TRIP-SET',
    name: 'Protection setting verification',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    scopes: ['breaker'],
    defaultSelected: true,
    section: 'Protection Settings',
    note: 'Compares the trip unit against the discrimination study. Needs the setting schedule loaded.',
    checks: [
      { label: 'Long-time setting matches the study', responseType: 'measured_pass_fail', unit: 'A', tolerance: 2, toleranceMode: 'percent', requiresPhoto: true },
      { label: 'Trip unit display photographed and legible', responseType: 'pass_fail_na', toleranceMode: 'none', requiresPhoto: true },
    ],
    shotCodes: ['TRIP-DISPLAY', 'TRIP-NAMEPLATE'],
  },
  {
    code: 'CB-OP',
    name: 'Breaker operation and status',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    breakerTypes: ['ACB'],
    scopes: ['breaker'],
    defaultSelected: true,
    section: 'CB Status',
    note: 'ACB states: ON, OFF, TRIP, TEST, NORMALISE, RACK-OUT — each photographed at the breaker, at the BMS position and at the BMS status change.',
    checks: [
      { label: 'Breaker operates through every state', responseType: 'action_reaction_result', expectedReaction: 'BMS position and status follow the breaker at every step', toleranceMode: 'none', requiresPhoto: true },
    ],
    shotCodes: [
      'CB-ON-BRK', 'CB-ON-POS', 'CB-ON-CHG',
      'CB-OFF-BRK', 'CB-OFF-POS', 'CB-OFF-CHG',
      'CB-TRIP-BRK', 'CB-TRIP-POS', 'CB-TRIP-CHG',
      'CB-TEST-BRK', 'CB-TEST-POS', 'CB-TEST-CHG',
      'CB-NORMALISE-BRK', 'CB-NORMALISE-POS', 'CB-NORMALISE-CHG',
      'CB-RACK-OUT-BRK', 'CB-RACK-OUT-POS', 'CB-RACK-OUT-CHG',
    ],
  },
  {
    code: 'CB-OP-MCCB',
    name: 'Breaker operation and status (MCCB)',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    breakerTypes: ['MCCB'],
    scopes: ['breaker'],
    defaultSelected: true,
    section: 'CB Status',
    note: 'MCCB states: ON, OFF, TRIP. An MCCB does not rack out, and asking for those photos is how a pending count lies.',
    checks: [
      { label: 'Breaker operates through every state', responseType: 'action_reaction_result', expectedReaction: 'BMS position and status follow the breaker at every step', toleranceMode: 'none', requiresPhoto: true },
    ],
    shotCodes: [
      'CB-ON-BRK', 'CB-ON-POS', 'CB-ON-CHG',
      'CB-OFF-BRK', 'CB-OFF-POS', 'CB-OFF-CHG',
      'CB-TRIP-BRK', 'CB-TRIP-POS', 'CB-TRIP-CHG',
    ],
  },
  {
    code: 'INTERLOCK',
    name: 'Interlocking and changeover',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    scopes: ['board', 'lineup'],
    defaultSelected: true,
    section: 'Interlocking Checks',
    checks: [
      { label: 'Mechanical interlock prevents parallel closure', responseType: 'action_reaction_result', expectedReaction: 'Second breaker cannot close while the first is closed', toleranceMode: 'none', requiresPhoto: true },
      { label: 'Changeover to alternate supply', responseType: 'action_reaction_result', expectedReaction: 'Load transfers within the specified time with no loss of supply', toleranceMode: 'none', requiresPhoto: true },
    ],
    shotCodes: ['INT-BEFORE', 'INT-AFTER'],
  },
  {
    code: 'THERMO',
    name: 'Thermographic survey',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    scopes: ['board'],
    defaultSelected: false,
    section: 'Thermography',
    note: 'Needs load on the board and a thermal camera. Off by default — select it when the job calls for it.',
    checks: [
      { label: 'Hottest joint above ambient', responseType: 'measured_pass_fail', unit: '°C', tolerance: 20, toleranceMode: 'max', acceptanceCriteria: '≤ 20 °C rise', requiresPhoto: true },
      { label: 'Busbar temperature', responseType: 'measured_pass_fail', unit: '°C', toleranceMode: 'none', acceptanceCriteria: 'Recorded value' },
    ],
    shotCodes: ['THERMO-BUSBAR', 'THERMO-INCOMER'],
  },

  /* --- Meter verification: the tests whose scope is the expensive question --- */
  {
    code: 'MV-3PH',
    name: 'Meter verification — three-phase voltage, current and frequency',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    scopes: ['board', 'lineup', 'breaker'],
    defaultSelected: true,
    section: 'Power Meter Reading Test',
    note: 'Compares DPM, PQA and BMS. Scope decides the photo count more than any other choice on this list.',
    checks: [
      { label: 'Voltage, current and frequency (3-phase)', responseType: 'meter_verification', unit: 'V', tolerance: 0.5, toleranceMode: 'percent' },
    ],
    shotCodes: mvShots('3Phase'),
  },
  {
    code: 'MV-1PH',
    name: 'Meter verification — single-phase voltage',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    scopes: ['board', 'lineup', 'breaker'],
    defaultSelected: true,
    section: 'Power Meter Reading Test',
    checks: [
      { label: 'Voltage (1-N)', responseType: 'meter_verification', unit: 'V', tolerance: 0.5, toleranceMode: 'percent' },
    ],
    shotCodes: mvShots('SinglePhaseVoltage'),
  },
  {
    code: 'MV-PWR',
    name: 'Meter verification — power and power factor',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    scopes: ['board', 'lineup', 'breaker'],
    defaultSelected: true,
    section: 'Power Meter Reading Test',
    checks: [
      { label: 'Active / apparent / reactive power and power factor', responseType: 'meter_verification', unit: 'kW', tolerance: 2, toleranceMode: 'percent' },
    ],
    shotCodes: mvShots('Power'),
  },
  {
    code: 'MV-VTHD',
    name: 'Meter verification — voltage THD',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    scopes: ['board', 'lineup', 'breaker'],
    defaultSelected: true,
    section: 'Power Meter Reading Test',
    checks: [
      { label: 'Voltage THD', responseType: 'meter_verification', unit: '%', tolerance: 5, toleranceMode: 'percent' },
    ],
    shotCodes: mvShots('VoltageTHD'),
  },
  {
    code: 'MV-ITHD',
    name: 'Meter verification — current THD',
    cxLevel: 'CxL3',
    equipmentTypes: ['LV Switchboard', 'MCC'],
    scopes: ['board', 'lineup', 'breaker'],
    defaultSelected: true,
    section: 'Power Meter Reading Test',
    checks: [
      { label: 'Current THD', responseType: 'meter_verification', unit: '%', tolerance: 5, toleranceMode: 'percent' },
    ],
    shotCodes: mvShots('CurrentTHD'),
  },
];

/** The meter-verification tests, which move together when the scope question is settled. */
/**
 * The five meter-verification groups, which move together when the scope
 * question is settled. Five groups x three sources = 15 photos per unit, which
 * is what the live shot profiles hold.
 */
export const METER_VERIFICATION_CODES = ['MV-3PH', 'MV-1PH', 'MV-PWR', 'MV-VTHD', 'MV-ITHD'] as const;
